import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  ShieldCheck, ShieldAlert, ShieldX, Loader2, Clock,
} from "lucide-react";
import { verifyCard, errorText } from "./identityCardApi";

/**
 * VerifyIdentityPage — what a scanned ID-card QR opens. Public, no login.
 *
 * ⚠️ THREE OUTCOMES, THREE COLOURS, AND THE MIDDLE ONE MATTERS MOST. A gate
 * guard glances at this for about a second, so the answer has to be readable
 * from arm's length and unambiguous:
 *
 *   valid + active    green   — genuine card, person is still with the institute
 *   valid + inactive  amber   — genuine card, person has LEFT or is suspended
 *   not valid         red     — forged, mistyped, or issued by another institute
 *
 * Collapsing the amber case into either neighbour is the failure that matters:
 * shown as green it admits someone who was removed; shown as red it accuses a
 * real card of being a fake.
 *
 * ⚠️ Renders OUTSIDE the app shell — no sidebar, no nav, no auth. It is opened
 * by a stranger's phone camera, and anything that needs a session would send
 * them to a login screen instead of an answer.
 *
 * ⚠️ It deliberately shows less than the card does. The server withholds email,
 * phone, course and batch; the photo is here because comparing it with the
 * printed one is what catches a reprint with a swapped picture.
 */

const VerifyIdentityPage = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    verifyCard(token)
      .then((data) => { if (!cancelled) setState({ status: "done", data }); })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: "done",
          data: {
            valid: false,
            message: errorText(err, "This card could not be verified."),
          },
        });
      });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <main className="tw-flex tw-min-h-screen tw-items-center tw-justify-center tw-bg-surface-alt tw-p-4">
      <div className="tw-w-full tw-max-w-[26rem]">
        {state.status === "loading"
          ? (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-rounded tw-border tw-border-solid tw-border-line-soft tw-bg-surface-raised tw-p-10">
              <Loader2 size={28} className="tw-animate-spin tw-text-primary" />
              <p className="tw-m-0 tw-text-sm tw-text-content-muted">Checking this card…</p>
            </div>
          )
          : <Result data={state.data} />}
      </div>
    </main>
  );
};

/** The three outcomes. See the component header for why they stay distinct. */
const OUTCOMES = {
  active: {
    icon: ShieldCheck,
    heading: "Valid card",
    sub: "This is a genuine card and the holder is currently with this institute.",
    band: "tw-bg-success",
    ink: "tw-text-on-status",
    tint: "tw-border-success/30",
  },
  inactive: {
    icon: ShieldAlert,
    heading: "Not currently valid",
    sub: "This card is genuine, but the holder's account is not active. Do not accept it as current.",
    band: "tw-bg-warning",
    ink: "tw-text-on-status",
    tint: "tw-border-warning/30",
  },
  invalid: {
    icon: ShieldX,
    heading: "Could not be verified",
    sub: "",
    band: "tw-bg-danger",
    ink: "tw-text-on-status",
    tint: "tw-border-danger/30",
  },
};

const Result = ({ data }) => {
  const key = !data?.valid ? "invalid" : data.active ? "active" : "inactive";
  const o = OUTCOMES[key];
  const Icon = o.icon;
  const holder = data?.holder;

  return (
    <div className={`tw-overflow-hidden tw-rounded tw-border tw-border-solid tw-bg-surface-raised ${o.tint}`}>
      <div className={`tw-flex tw-items-center tw-gap-3 tw-px-5 tw-py-4 ${o.band} ${o.ink}`}>
        <Icon size={28} className="tw-shrink-0" />
        <div className="tw-min-w-0">
          <h1 className="tw-m-0 tw-text-lg tw-font-semibold tw-leading-tight">{o.heading}</h1>
          <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-leading-snug tw-opacity-90">
            {o.sub || data?.message}
          </p>
        </div>
      </div>

      {data?.valid && holder && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-p-6">
          <div className="tw-h-28 tw-w-28 tw-overflow-hidden tw-rounded-full tw-border-4 tw-border-solid tw-border-surface-raised tw-bg-surface-sunken tw-shadow">
            {holder.photoUrl
              ? <img src={holder.photoUrl} alt="" className="tw-h-full tw-w-full tw-object-cover" />
              : (
                <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-3xl tw-font-semibold tw-text-content-subtle">
                  {(holder.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
          </div>

          <div className="tw-text-center">
            <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content">{holder.name}</p>
            <p className="tw-m-0 tw-mt-0.5 tw-text-sm tw-text-content-muted">{holder.roleLabel}</p>
          </div>

          {data.institute && (
            <div className="tw-mt-1 tw-flex tw-items-center tw-gap-2 tw-rounded-sm tw-border tw-border-solid tw-border-line-soft tw-bg-surface-sunken tw-px-3 tw-py-2">
              {data.institute.logoUrl && (
                <img src={data.institute.logoUrl} alt=""
                  className="tw-h-6 tw-w-6 tw-shrink-0 tw-rounded-sm tw-object-contain" />
              )}
              <span className="tw-text-sm tw-font-medium tw-text-content">{data.institute.name}</span>
            </div>
          )}

          {data.memberSince && (
            <p className="tw-m-0 tw-text-xs tw-text-content-subtle">
              Member since {new Date(data.memberSince).toLocaleDateString("en-IN", {
                year: "numeric", month: "long", timeZone: "Asia/Kolkata",
              })}
            </p>
          )}
        </div>
      )}

      <div className="tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-px-5 tw-py-3">
        <p className="tw-m-0 tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-subtle">
          <Clock size={12} />
          {/* Stamped so a screenshot of this page cannot be passed off later as
              a live check — the answer is only true at the moment it was asked. */}
          Checked {data?.checkedAt
            ? new Date(data.checkedAt).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })
            : "just now"}
        </p>
      </div>
    </div>
  );
};

export default VerifyIdentityPage;
